import React, { useState, useEffect } from 'react';
import { MdPlayArrow, MdRefresh, MdPeople, MdArrowBack } from 'react-icons/md';
import { uuid } from 'uuidv4';
import { useQueryClient } from 'react-query';
import { can } from '../../../permissions';
import Attention from '../../ReusableComponents/Attention';
import PortalButton from './PortalButton';
import PortalFormModal from './PortalFormModal';
import { usePortal, usePortalQuery, usePortalMutation } from '../hooks';
import { ErrorNotice, Status, Loading } from './Feedback';
import Pagination from './Pagination';

const actionLabels = {
  approve: 'Deferir inscrições',
  reject: 'Indeferir inscrições',
  convert: 'Cadastrar candidatos na OnTest',
};
export default function OperationPanel({ filters, selected = [], single = false }) {
  const cache = useQueryClient();
  const { user, faculdade } = usePortal();
  const [open, setOpen] = useState(false),
    [preview, setPreview] = useState(null),
    [id, setId] = useState(''),
    [count, setCount] = useState(''),
    [page, setPage] = useState(1),
    [error, setError] = useState(null);
  const [scope, setScope] = useState({ filters, ids: [...selected] });
  const [inputs, setInputs] = useState({
    action: single || !can(user, 'portal_inscricoes', 'edit') ? 'convert' : 'approve',
    scope: selected.length ? 'selected' : 'filtered',
    reason: single ? 'Cadastro do candidato deferido solicitado pelo administrador.' : '',
  });
  const totals = usePortalQuery(
    'operacoes/totais?' +
      new URLSearchParams(
        Object.entries(scope.filters).filter(([, value]) => value !== '' && value != null)
      ),
    { enabled: open && !single }
  );
  const query = usePortalQuery(`operacoes/${id}?page=${page}`, {
    enabled: !!id && open,
    poll: !!id && open,
  });
  const create = usePortalMutation('operacoes/previa'),
    execute = usePortalMutation(`operacoes/${preview?.id}/executar`),
    retry = usePortalMutation(`operacoes/${id}/retentar`);
  const op = query.data?.data;
  const state = op?.status;
  const busy = create.isLoading || execute.isLoading || retry.isLoading;
  useEffect(() => {
    if (!['complete', 'partial', 'failed'].includes(state)) return;
    cache.invalidateQueries(['portal', faculdade, user?.id, 'inscricoes']);
    cache.invalidateQueries(['portal', faculdade, user?.id, 'inscricao']);
    cache.invalidateQueries({
      queryKey: ['portal', faculdade, user?.id, 'request'],
      predicate: (entry) => String(entry.queryKey[4]).startsWith('operacoes/totais?'),
    });
  }, [state, id, cache, faculdade, user?.id]);
  const close = () => {
    if (!id) setPreview(null);
    setOpen(false);
  };
  return (
    <>
      <PortalButton
        className={
          single
            ? 'btn btn-success hidethis'
            : id
            ? 'btn btn-warning hidethis'
            : 'btn btn-primary hidethis'
        }
        disabled={!create.allowed && !id}
        onClick={() => {
          if (!id && !preview) {
            setScope({ filters: { ...filters }, ids: [...selected] });
            setInputs((current) => ({
              ...current,
              scope: selected.length ? 'selected' : 'filtered',
            }));
          }
          setOpen(true);
        }}
      >
        <MdPeople />
        {single ? 'Cadastrar na OnTest' : id ? 'Ver operação em lote' : 'Ações em lote'}
      </PortalButton>
      {open && (
        <PortalFormModal
          title={single ? 'Cadastro na OnTest' : 'Ações em lote'}
          description={
            single
              ? 'Confira e confirme o cadastro deste candidato.'
              : 'Escolha a ação, confira as inscrições e acompanhe o processamento.'
          }
          icon={<MdPeople />}
          onClose={close}
          busy={busy}
          allowed={id ? true : preview ? execute.allowed : create.allowed}
          error={error || create.error || execute.error || retry.error}
          requiredNote={!id}
          submitText={id ? null : preview ? 'Confirmar processamento' : 'Conferir inscrições'}
          submitIcon={<MdPlayArrow />}
          loadingText="Processando solicitação…"
          submitDisabled={
            !!preview && (!preview.total || !count.trim() || Number(count) !== preview.total)
          }
          onSubmit={(event) => {
            if (preview) {
              execute.mutate(
                { confirmed_count: Number(count), idempotency_key: preview.key },
                {
                  onSuccess: (response) => {
                    setId(response.data.id);
                    setPage(1);
                    setPreview(null);
                  },
                }
              );
              return;
            }
            const form = new FormData(event.currentTarget);
            const values = {
              action: form.get('action'),
              scope: form.get('scope'),
              reason: form.get('reason'),
            };
            if (values.scope === 'selected' && !scope.ids.length) {
              setError(new Error('Selecione pelo menos uma inscrição.'));
              return;
            }
            setError(null);
            setInputs(values);
            create.mutate(
              { ...values, ids: scope.ids, filters: scope.filters },
              {
                onSuccess: (response) => {
                  setPreview({ ...response.data, ...values, key: uuid() });
                  setCount('');
                },
              }
            );
          }}
        >
          {id ? (
            query.isLoading ? (
              <Loading />
            ) : op ? (
              <section className="portal-operation-progress" role="status">
                <Status value={op.status} />
                <h3>
                  {op.succeeded} concluída(s) de {op.total}
                </h3>
                <p>{op.failed} inscrição(ões) com falha.</p>
                <progress value={op.succeeded + op.failed} max={op.total || 1} />
                <ErrorNotice error={query.error} />
                {op.error && <div className="alert alert-warning">{op.error}</div>}
                {['partial', 'failed'].includes(op.status) && (
                  <PortalButton
                    className="btn btn-primary"
                    disabled={!retry.allowed || retry.isLoading}
                    onClick={() => retry.mutate({})}
                  >
                    <MdRefresh /> Reprocessar pendentes e falhas
                  </PortalButton>
                )}
                {query.data?.failures?.data?.map((failure) => (
                  <p key={failure.id}>
                    {failure.application_id} · {failure.error}
                  </p>
                ))}
                <Pagination meta={query.data?.failures?.meta} onPage={setPage} />
                {['complete', 'partial', 'failed'].includes(op.status) && (
                  <PortalButton
                    className="btn btn-success"
                    onClick={() => {
                      setId('');
                      setPreview(null);
                      setCount('');
                      setScope({ filters: { ...filters }, ids: [...selected] });
                      setInputs((current) => ({
                        ...current,
                        scope: selected.length ? 'selected' : 'filtered',
                      }));
                    }}
                  >
                    <MdPeople /> Nova ação
                  </PortalButton>
                )}
              </section>
            ) : (
              <ErrorNotice
                error={query.error || new Error('Não foi possível carregar a operação.')}
              />
            )
          ) : preview ? (
            <section className="portal-operation-confirmation">
              <h3>{actionLabels[preview.action]}</h3>
              <div className="portal-operation-total">
                <strong>{preview.total}</strong>
                <span>inscrição(ões) na prévia</span>
              </div>
              <p>
                Confira a quantidade antes de confirmar. O processamento utiliza as inscrições desta
                prévia.
              </p>
              <label className="form-label">
                <span>
                  Digite {preview.total} para confirmar <Attention />
                </span>
                <input
                  className="form-control"
                  inputMode="numeric"
                  required
                  value={count}
                  onChange={(event) => setCount(event.target.value)}
                />
              </label>
              {!preview.total && (
                <div className="alert alert-info">Nenhuma inscrição elegível para esta ação.</div>
              )}
              <PortalButton
                className="btn btn-info"
                onClick={() => {
                  setPreview(null);
                  setCount('');
                }}
              >
                <MdArrowBack /> Alterar ação
              </PortalButton>
            </section>
          ) : (
            <>
              {!single && (
                <>
                  <div className="portal-admin-facts">
                    {[
                      ['total', 'Inscrições nos filtros'],
                      ['approved', 'Deferidas'],
                      ['synced', 'Cadastradas'],
                      ['failed', 'Falhas'],
                    ].map(([key, label]) => (
                      <div key={key}>
                        <small>{label}</small>
                        <strong>{totals.data?.data?.[key] ?? '—'}</strong>
                      </div>
                    ))}
                  </div>
                  <ErrorNotice error={totals.error} />
                </>
              )}
              <p className="form-text">
                Deferir uma inscrição e cadastrar o candidato na OnTest são ações separadas.
              </p>
              {single ? (
                <>
                  <input type="hidden" name="action" value="convert" />
                  <input type="hidden" name="scope" value="selected" />
                </>
              ) : (
                <div className="portal-admin-grid">
                  <label className="form-label">
                    <span>
                      Ação <Attention />
                    </span>
                    <select
                      name="action"
                      className="form-control form-select"
                      required
                      defaultValue={inputs.action}
                    >
                      {can(user, 'portal_inscricoes', 'edit') && (
                        <>
                          <option value="approve">Deferir inscrições</option>
                          <option value="reject">Indeferir inscrições</option>
                        </>
                      )}
                      {can(user, 'portal_integracoes', 'edit') && (
                        <option value="convert">Cadastrar candidatos na OnTest</option>
                      )}
                    </select>
                  </label>
                  <label className="form-label">
                    <span>
                      Aplicar em <Attention />
                    </span>
                    <select
                      name="scope"
                      className="form-control form-select"
                      required
                      defaultValue={inputs.scope}
                    >
                      <option value="selected" disabled={!scope.ids.length}>
                        Selecionadas ({scope.ids.length})
                      </option>
                      <option value="filtered">Correspondentes aos filtros</option>
                    </select>
                  </label>
                </div>
              )}
              <label className="form-label">
                <span>
                  Justificativa <Attention />
                </span>
                <textarea
                  name="reason"
                  required
                  minLength={5}
                  maxLength={10000}
                  className="form-control"
                  rows={3}
                  defaultValue={inputs.reason}
                />
              </label>
            </>
          )}
        </PortalFormModal>
      )}
    </>
  );
}
